import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useEdiProfile } from '@/api/hanly-v2/edi/edi';
import type { EdiPartner, EdiProfile } from '@/api/hanly-v2/hanlyAPIV2.schemas';
import { buildAnimalIdLogoutUrl, getAnimalIdConfig } from '@/features/auth/animal-id';
import { clearSavedAuth, ID_TOKEN_KEY, PARTNER_ID_KEY } from '@/libs/auth-storage';
import { buildSigninPath, SIGNIN_PATH } from '@/utils/return-to';

interface AuthContextType {
  profile: EdiProfile | null;
  partners: readonly EdiPartner[];
  selectedPartnerId: number | null;
  setSelectedPartnerId: (id: number) => void;
  isLoading: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/** 選択中の取引先の発注先が料金(単価・金額・合計)を表示するか。表示しない発注先では API も料金を null で返す */
export const useShowPrices = () => {
  const { partners, selectedPartnerId } = useAuth();
  return partners.find((partner) => partner.id === selectedPartnerId)?.show_prices ?? true;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const queryClient = useQueryClient();
  const [selectedPartnerId, setSelectedPartnerIdState] = useState<number | null>(null);
  const token = localStorage.getItem('accessToken');

  const {
    data: profile,
    isLoading,
    isError,
  } = useEdiProfile({
    query: {
      enabled: !!token,
      retry: false,
    },
  });

  const logout = useCallback(() => {
    const idToken = localStorage.getItem(ID_TOKEN_KEY);
    clearSavedAuth();
    queryClient.clear();
    // 明示的なログアウトなので戻り先は引き継がない。
    // アニマルID へ移る前にログイン画面にしておき、ブラウザの「戻る」で前の利用者の画面が出ないようにする
    navigate(SIGNIN_PATH);
    const animalId = getAnimalIdConfig();
    if (animalId && idToken) {
      // アニマルID でログインしていたら、アニマルID からもログアウトする。
      // 共用の端末で、次の人がパスワードを入れずに前の人としてログインできないようにする
      window.location.assign(buildAnimalIdLogoutUrl(animalId, idToken));
    }
  }, [navigate, queryClient]);

  // 未認証時はサインインへリダイレクト
  useEffect(() => {
    if (!token || isError) {
      clearSavedAuth();
      queryClient.clear();
      // 通知メールのリンク等で未ログインのまま来た場合に、再ログイン後へ戻り先を引き継ぐ
      navigate(buildSigninPath(`${pathname}${search}`), { replace: true });
    }
  }, [token, isError, navigate, queryClient, pathname, search]);

  // プロフィール取得後に取引先を初期選択
  useEffect(() => {
    if (!profile?.partners?.length) return;

    const storedId = localStorage.getItem(PARTNER_ID_KEY);
    const storedPartnerId = storedId ? Number(storedId) : null;

    if (storedPartnerId && profile.partners.some((p) => p.id === storedPartnerId)) {
      setSelectedPartnerIdState(storedPartnerId);
    } else {
      const firstId = profile.partners[0].id;
      setSelectedPartnerIdState(firstId);
      localStorage.setItem(PARTNER_ID_KEY, String(firstId));
    }
  }, [profile]);

  const setSelectedPartnerId = useCallback(
    (id: number) => {
      setSelectedPartnerIdState(id);
      localStorage.setItem(PARTNER_ID_KEY, String(id));
      queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const value = useMemo(
    () => ({
      profile: profile ?? null,
      partners: profile?.partners ?? [],
      selectedPartnerId,
      setSelectedPartnerId,
      isLoading,
      logout,
    }),
    [profile, selectedPartnerId, setSelectedPartnerId, isLoading, logout],
  );

  if (!token) return null;
  if (isLoading || (profile?.partners?.length && !selectedPartnerId)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-neutral-500">読み込み中...</p>
      </div>
    );
  }

  return <AuthContext value={value}>{children}</AuthContext>;
};
